import * as Popover from '@radix-ui/react-popover';
import React from 'react';

import ImportantSvg from '@/components/common/ImportantSvg';
import TooltipClose from '@/components/common/TooltipCloseSvg';
interface CardExpiryWarningProps {
    expiresInDays: number;
    portalID?: string;
    collisionBoundary?: string;
    hideWhenDetached?: boolean;
    modal?: boolean;
}

export const CardExpiryWarning: React.FC<CardExpiryWarningProps> = ({
    expiresInDays,
    portalID,
    collisionBoundary,
    hideWhenDetached,
    modal,
}) => {
    // Return nothing if -1
    if (expiresInDays === -1) {
        return null;
    }

    // Determine fill color based on days
    let fillColor: string;
    let message: React.ReactNode;

    if (expiresInDays === 0) {
        fillColor = '#bd2f2f'; // Dark red for expired
        message = (
            <>
                <b className="font-semibold">Your credit card has expired.</b> Please call{' '}
                <a href="tel:1-888-2020-888" className="hover:underline">
                    1-888-2020-888
                </a>{' '}
                to update it as soon as possible and avoid disruption to your shopping experience.
            </>
        );
    } else if (expiresInDays === 30) {
        fillColor = '#f8bb01'; // Yellow for expiring soon
        message = (
            <>
                <b className="font-semibold">Your credit card will expire within 30 days or less.</b> Please call{' '}
                <a href="tel:1-888-2020-888" className="hover:underline">
                    1-888-2020-888
                </a>{' '}
                to update it as soon as possible and avoid disruption to your shopping experience.
            </>
        );
    } else if (expiresInDays === 60) {
        fillColor = '#037c8b'; // Green for 60 days
        message = (
            <>
                <b className="font-semibold">Your credit card will expire in 60 days or less.</b> Please call{' '}
                <a href="tel:1-888-2020-888" className="hover:underline">
                    1-888-2020-888
                </a>{' '}
                to update it as soon as possible.
            </>
        );
    } else {
        // If not one of the expected values, don't show anything
        return null;
    }

    // Get the portal container if portalID is provided
    const portalContainer = portalID ? document.getElementById(portalID) : undefined;
    const collisionBoundaryContainer = collisionBoundary ? document.getElementById(collisionBoundary) : undefined;

    return (
        <Popover.Root modal={modal}>
            <Popover.Trigger className="h-5 w-6">
                <span className="visually-hidden">Card expiry warning</span>
                <div className="h-5 w-5">
                    <ImportantSvg fill={fillColor} className="h-5 w-5" style={{ marginTop: '1px' }} />
                </div>
            </Popover.Trigger>
            <Popover.Portal container={portalContainer}>
                <Popover.Content
                    collisionBoundary={collisionBoundaryContainer}
                    hideWhenDetached={hideWhenDetached}
                    className="max-w-72 rounded-[2px] border border-solid border-black bg-white p-4 pr-7 text-xsm shadow-lg"
                >
                    {message}
                    <Popover.Close className="absolute right-2 top-2">
                        <span className="sr-only">Close</span>
                        <TooltipClose />
                    </Popover.Close>
                    <Popover.Arrow />
                </Popover.Content>
            </Popover.Portal>
        </Popover.Root>
    );
};
